import { useEffect, useState } from "react";
import { MessageSquareOff } from "lucide-react";
import { cn } from "~/lib/utils";
import { FormDialog } from "~/pages/manifold-nextgen/components/FormDialog";
import { AgentAvatar } from "~/pages/manifold-nextgen/components/agents/AgentAvatar";
import { useAsyncMutation } from "~/pages/manifold-nextgen/hooks/useAsyncMutation";
import { Agent } from "~/services/agents";
import { createProjectConversation } from "~/services/projects";

interface NewConversationDialogProps {
  projectId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  attachedAgents: Agent[];
  onCreated?: (conversationId: string) => void;
}

export const NewConversationDialog = ({
  projectId,
  open,
  onOpenChange,
  attachedAgents,
  onCreated,
}: NewConversationDialogProps) => {
  const [selectedAgentId, setSelectedAgentId] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setSelectedAgentId(attachedAgents.length === 1 ? attachedAgents[0].id : null);
    }
  }, [open, attachedAgents]);

  const { execute, isPending } = useAsyncMutation(
    (agentId: string) =>
      createProjectConversation(projectId, {
        agent_id: agentId,
        user_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      }),
    {
      errorMessage: "Failed to start conversation",
      onSuccess: (conversation) => {
        onCreated?.(conversation.id);
        onOpenChange(false);
      },
    }
  );

  const hasAgents = attachedAgents.length > 0;

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title="New conversation"
      description="Pick an agent to start a conversation with."
      contentClassName="sm:max-w-[560px]"
      onPrimary={() => selectedAgentId && void execute(selectedAgentId)}
      isPending={isPending}
      primaryLabel="Start"
      primaryPendingLabel="Starting…"
      primaryDisabled={!hasAgents || !selectedAgentId}
    >
      {hasAgents ? (
        <ul className="grid max-h-[50vh] grid-cols-3 gap-2 overflow-y-auto p-1" role="list">
          {attachedAgents.map((agent) => (
            <AgentOption
              key={agent.id}
              agent={agent}
              selected={selectedAgentId === agent.id}
              onSelect={() => setSelectedAgentId(agent.id)}
            />
          ))}
        </ul>
      ) : (
        <div role="status" className="flex flex-col items-center gap-2 px-2 py-8 text-center">
          <MessageSquareOff className="size-8 text-muted-foreground" aria-hidden="true" />
          <p className="text-sm font-medium text-foreground">No agents attached</p>
          <p className="max-w-xs text-xs text-muted-foreground">
            Attach an agent to this project before starting a conversation.
          </p>
        </div>
      )}
    </FormDialog>
  );
};

interface AgentOptionProps {
  agent: Agent;
  selected: boolean;
  onSelect: () => void;
}

const AgentOption = ({ agent, selected, onSelect }: AgentOptionProps) => (
  <li>
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      className={cn(
        "flex w-full flex-col items-center gap-2 rounded-lg border border-transparent px-2 py-3",
        "ring-1 ring-border/60 transition-all",
        "hover:bg-accent/40 hover:ring-border",
        selected && "bg-accent/40 ring-2 ring-primary hover:ring-primary"
      )}
    >
      <AgentAvatar id={agent.id} name={agent.name} size={48} />
      <p className="line-clamp-2 text-center text-xs font-medium text-foreground">
        {agent.display_name}
      </p>
    </button>
  </li>
);
